import { Component, Input, Output, EventEmitter, OnInit, ViewChildren, QueryList, ElementRef, forwardRef, HostListener, OnChanges, SimpleChanges, Self, ViewChild, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { ExternalPasswordStyle } from '@app/_enums/externalView';
import { ErrorHandlerService } from '@app/_services/error-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Subject, debounceTime } from 'rxjs';

@Component({
  selector: 'kupari-multi-box-input',
  templateUrl: './kupari-multi-box-input.component.html',
  styleUrls: ['./kupari-multi-box-input.component.scss'],
})
export class KupariMultiBoxInputComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit {
  @Input() length = 8;
  @Input() style = ExternalPasswordStyle.Numbers;
  @Input() disabled = false;
  @Input() type: 'text' | 'number' = 'text';

  @Input() messagePosition: string = 'bottom';
  @Input() validationRule: boolean = null;
  @Input() validationMessage: string = null;
  @Input() validation: { rule: boolean, message: string, params?: string }[];

  @Output() valueChange = new EventEmitter<string>();
  @Output() onFilled = new EventEmitter<void>();

  @ViewChild('hiddenInput') hiddenInput!: ElementRef<HTMLInputElement>;

  private filledSubject = new Subject<void>();

  value = ''; // always kept at fixed `length` with spaces
  caretIndex = 0;

  onChange = (v: string) => {};
  onTouched = () => {};
  isComposing = false;

  // Allowed & replace rules; kept in sync in ngOnChanges
  allowedChars = /^[0-9 ]$/;
  replaceChars = /[^0-9]/g;

  constructor(
    @Self() public controlDirective: NgControl,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    private store: Store
  ) {
    controlDirective.valueAccessor = this;
  }

  ngOnInit() {
    // Ensure validators run
    const control = this.controlDirective.control;
    control.setValidators(control.validator);
    control.updateValueAndValidity();

    // Debounced onFilled
    this.filledSubject.pipe(debounceTime(500)).subscribe(() => this.onFilled.emit());

    // Initialize fixed-length value
    this.ensureFixedLength();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['length'] && changes['length'].currentValue !== changes['length'].previousValue) {
      this.ensureFixedLength();
      this.caretIndex = Math.min(this.caretIndex, this.length - 1);
      this.syncCaret();
    }
    if (changes['style'] && changes['style'].currentValue !== changes['style'].previousValue) {
      switch (changes['style'].currentValue) {
        case ExternalPasswordStyle.Numbers:
          this.allowedChars = /^[0-9 ]$/;
          this.replaceChars = /[^0-9]/g;
          break;
        case ExternalPasswordStyle.Characters:
          this.allowedChars = /^[a-zA-Z ]$/;
          this.replaceChars = /[^a-zA-Z]/g; // FIX: remove non-letters
          break;
        case ExternalPasswordStyle.NumbersAndCharacters:
          this.allowedChars = /^[a-zA-Z0-9 ]$/;
          this.replaceChars = /[^0-9a-zA-Z]/g;
          break;
        default:
          this.allowedChars = /^[0-9 ]$/;
          this.replaceChars = /[^0-9]/g;
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.disabled) {
        this.focusInput();
        this.caretIndex = 0;
        this.syncCaret();
      }
    });
  }

  // ControlValueAccessor
  writeValue(val: string | null): void {
    const incoming = (val ?? '').replace(this.replaceChars, '');
    this.value = (incoming + ' '.repeat(this.length)).slice(0, this.length);
    this.emit(false);
  }
  registerOnChange(fn: (val: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }

  // Utils
  private ensureFixedLength() {
    this.value = (this.value || '');
    if (this.value.length < this.length) {
      this.value = this.value.padEnd(this.length, ' ');
    } else if (this.value.length > this.length) {
      this.value = this.value.slice(0, this.length);
    }
  }

  private emit(checkFilled = true) {
    const out = this.value;
    this.onChange(out);
    this.onTouched();
    this.valueChange.emit(out);

    if (checkFilled) {
      const count = out.replace(this.replaceChars, '').trim().length;
      if (count === this.length) this.filledSubject.next();
    }
  }

  focusInput() {
    if (this.disabled) return;
    this.hiddenInput?.nativeElement.focus();
  }

  // Visual state for boxes
  activeIndex(idx: number): boolean { return idx === this.caretIndex; }

  // Clicking a box moves caret there
  selectIndex(idx: number) {
    if (this.disabled) return;
    this.focusInput();
    this.caretIndex = Math.max(0, Math.min(idx, this.length - 1));
    this.syncCaret();
  }

  private syncCaret() {
    const el = this.hiddenInput?.nativeElement;
    if (!el) return;
    // Select the single box under caret for clear visual feedback
    el.setSelectionRange(this.caretIndex, this.caretIndex + 1);
  }

  // ---- Keyboard handling (single source of truth) ----

  onKeyDown(e: KeyboardEvent) {
    if (this.disabled) return;

    const key = e.key;

    if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === 'v') {
      return; // onPaste will handle
    }
    if (e.shiftKey && key === 'Insert') {
      return; // Shift+Insert = paste
    }

    // We handle everything ourselves -> prevent default where applicable.
    const isPrintable = key.length === 1;
    const navKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    const editKeys = ['Backspace', 'Delete'];

    if (isPrintable || navKeys.includes(key) || editKeys.includes(key)) {
      e.preventDefault();
    }

    // Navigation
    if (key === 'ArrowLeft') {
      this.caretIndex = Math.max(0, this.caretIndex - 1);
      return this.syncCaret();
    }
    if (key === 'ArrowRight') {
      this.caretIndex = Math.min(this.length - 1, this.caretIndex + 1);
      return this.syncCaret();
    }
    if (key === 'Home') {
      this.caretIndex = 0;
      return this.syncCaret();
    }
    if (key === 'End') {
      // jump to last filled or last index
      const lastFilled = Math.max(0, this.value.replace(/ +$/,'').length - 1);
      this.caretIndex = Math.min(lastFilled, this.length - 1);
      return this.syncCaret();
    }

    // Edit
    if (key === 'Backspace') {
      if (this.caretIndex > 0) {
        this.caretIndex -= 1;
        this.value = this.value.slice(0, this.caretIndex) + ' ' + this.value.slice(this.caretIndex + 1);
        this.emit();
        return this.syncCaret();
      }
      return;
    }

    if (key === 'Delete') {
      this.value = this.value.slice(0, this.caretIndex) + ' ' + this.value.slice(this.caretIndex + 1);
      this.emit();
      return this.syncCaret();
    }

    // Printable input
    if (isPrintable && this.allowedChars.test(key)) {
      const ch = key.replace(this.replaceChars, ''); // enforce style
      if (!ch) return;
      this.value = this.value.slice(0, this.caretIndex) + ch + this.value.slice(this.caretIndex + 1);
      if (this.caretIndex < this.length - 1) this.caretIndex += 1;
      this.emit();
      return this.syncCaret();
    }
  }

  onPaste(e: ClipboardEvent) {
    if (this.disabled) return;
    const text = e.clipboardData?.getData('text') ?? '';
    if (!text) return;
    e.preventDefault();

    const clean = text.split('').filter(c => this.allowedChars.test(c)).join('').replace(this.replaceChars, '');
    if (!clean) return;

    let idx = this.caretIndex;
    const chars = this.value.split('');
    for (const c of clean) {
      if (idx >= this.length) break;
      chars[idx++] = c;
    }
    this.value = chars.join('');
    this.caretIndex = Math.min(idx, this.length - 1);
    this.emit();
    this.syncCaret();
  }
  clear() {
    this.caretIndex = 0;
    this.value = ' '.repeat(this.length);
    this.emit();
    this.syncCaret();
  }

onCompositionStart() {
  this.isComposing = true;  // bufferointi IME:lle (esim. monikirjaimiset syötteet)
}

onCompositionEnd(e: CompositionEvent) {
  this.isComposing = false;
  const data = (e as any).data ?? '';
  if (!data) return;
  // Commit IME-tuotos kerralla
  this.insertText(data);
}

// Android/Gboard pääreitti
onBeforeInput(e: InputEvent) {
  if (this.disabled) return;
  // Jos IME käynnissä, odota compositionEndiä
  if (this.isComposing) return;

  const t = e.inputType;

  // Tekstin lisäys (yksi tai useampi merkki)
  if (t === 'insertText') {
    const data = e.data ?? '';
    if (!data) return;
    // Salli vain valitut merkit
    if (![...data].every(ch => this.allowedChars.test(ch))) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    this.insertText(data);
    return;
  }

  // Liimaus (osa selaimista kuljettaa tämän beforeinputissa)
  if (t === 'insertFromPaste') {
    e.preventDefault();
    // Varsinainen data tulee (paste)-eventissä luotettavammin -> ei tehdä mitään tässä
    return;
  }

  // Poistot
  if (t === 'deleteContentBackward') {
    e.preventDefault();
    this.deleteBackward();
    return;
  }
  if (t === 'deleteContentForward') {
    e.preventDefault();
    this.deleteForward();
    return;
  }
}

// Fallback jos selain kuitenkin muuttaa <input>in arvoa
onNativeInput(ev: Event) {
  const el = ev.target as HTMLInputElement;
  if (el.value !== this.value) {
    // Palauta ohjaukseen: me omistamme arvon
    el.value = this.value;
    this.syncCaret();
  }
}

/** Apurit: kirjoitus ja poistot **/
private insertText(text: string) {
  // suodata varmuuden vuoksi
  const clean = [...text].filter(ch => this.allowedChars.test(ch)).join('');
  if (!clean) return;

  let idx = this.caretIndex;
  const chars = this.value.split('');
  for (const ch of clean) {
    if (idx >= this.length) break;
    chars[idx++] = ch;
  }
  this.value = chars.join('');
  this.caretIndex = Math.min(idx, this.length - 1);
  this.emit();
  this.syncCaret();
}

private deleteBackward() {
  if (this.caretIndex > 0) {
    this.caretIndex -= 1;
    this.value = this.value.slice(0, this.caretIndex) + ' ' + this.value.slice(this.caretIndex + 1);
    this.emit();
    this.syncCaret();
  }
}

private deleteForward() {
  this.value = this.value.slice(0, this.caretIndex) + ' ' + this.value.slice(this.caretIndex + 1);
  this.emit();
  this.syncCaret();
}


}
